import { FC, useState } from 'react'
import { Box, Button, Typography, TextField } from '@mui/material'
import { PatientUser } from '~/shared/types'

import { FFModal } from '~/shared/ui'
import { usePatientDetail } from '~/features/member-profile'
import { useUpdateEmail, useTriggerOtp, useOtpVerification } from './utils'

interface EmailChangeModelProps {
  handleClose: () => void
  open: boolean
  patient: PatientUser
}

const EmailChangeModel: FC<EmailChangeModelProps> = props => {
  const [emailState, setEmailState] = useState({ title: 'Change Member Email', page: 'send_otp' })
  const [error, setError] = useState(false)
  const { id } = usePatientDetail()
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const { run: sendOtpHandler, loading: sendOtpLoading } = useTriggerOtp()
  const { run: verifyOtpHandler, loading: verifyOtpLoading } = useOtpVerification()
  const { run: saveEmailHandler, loading: saveEmailLoading } = useUpdateEmail()

  // The step bodies are render functions, not nested components: defining a
  // component type inside render remounts its subtree whenever this component
  // re-renders (e.g. a provider above it settles a query), detaching the DOM
  // mid-interaction and resetting in-progress input.
  const renderSendOtp = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', px: 4, py: 6 }}>
          <Typography variant="h6">{emailState.title}</Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography>
              For security, we have to confirm the member’s identity before updating their email
              address. Here’s what happens:
            </Typography>
          </Box>
          <Typography>1. We text a security code to the member’s phone number on file</Typography>
          <Typography>2. Member reads code back to us over the phone</Typography>
          <Typography>3. We ask the member for their new email address</Typography>
          <Typography>4. We automatically send member a verification email</Typography>
          <Typography>
            5. Member follows the link in the verification email to verify their new email address
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 4, py: 6 }}>
          <Box sx={{ mr: 1 }}>
            <Button disabled={sendOtpLoading} onClick={props.handleClose} color="primary">
              Cancel
            </Button>
          </Box>
          <Button
            onClick={() => sendOtpHandler(id, setEmailState, setError)}
            disabled={sendOtpLoading}
            color="primary"
            variant="outlined"
          >
            Text Code to Member
          </Button>
        </Box>
      </Box>
    )
  }

  const renderVerifyOtp = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 6, px: 4 }}>
          <Typography variant="h6">{emailState.title}</Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography>
              {`We texted a 6-digit security code to ${props.patient.phone_number}. It will arrive shortly. Ask the member to read it back to you and enter it here.`}
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Enter 6-digit code"
            error={error}
            value={otp}
            helperText={error ? 'Please check the entered OTP' : ''}
            sx={{ '&:hover span': { opacity: 1 } }}
            variant="outlined"
            onChange={e => setOtp(e.target.value)}
          />
          <Box
            sx={{
              mt: 3,
              mb: 2,
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography>Member didn’t receive a code?</Typography>
            <Button
              disabled={verifyOtpLoading}
              onClick={() => sendOtpHandler(id, setEmailState, setError)}
              color="primary"
              style={{ fontSize: '1.5rem' }}
            >
              Resend
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 4, py: 6 }}>
          <Box sx={{ mr: 1 }}>
            <Button disabled={verifyOtpLoading} onClick={props.handleClose} color="primary">
              Cancel
            </Button>
          </Box>
          <Button
            onClick={() => verifyOtpHandler(id, otp, setEmailState, setError)}
            disabled={verifyOtpLoading}
            color="primary"
            variant="outlined"
          >
            Continue
          </Button>
        </Box>
      </Box>
    )
  }

  const renderUpdateEmail = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 6, px: 4 }}>
          <Typography variant="h6">{emailState.title}</Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography>
              The security code is correct. Ask the member for their new email address and enter it
              here.
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="New email address"
            value={email}
            error={error}
            helperText={error ? 'Invalid Email' : ''}
            sx={{ '&:hover span': { opacity: 1 } }}
            variant="outlined"
            onChange={e => setEmail(e.target.value)}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 6, px: 4 }}>
          <Box sx={{ mr: 1 }}>
            <Button disabled={saveEmailLoading} onClick={props.handleClose} color="primary">
              Cancel
            </Button>
          </Box>
          <Button
            onClick={() => {
              saveEmailHandler(props.patient.id, email, setEmailState, setError)
            }}
            disabled={saveEmailLoading}
            color="primary"
            variant="outlined"
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  const renderSuccess = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 6, px: 4 }}>
          <Typography variant="h6">{emailState.title}</Typography>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography>
              We sent a verification email to the member. Ask them to check that they received it
              and to follow the link inside to verify their email address.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 6, px: 4 }}>
            <Button
              disabled={saveEmailLoading}
              onClick={props.handleClose}
              color="primary"
              variant="outlined"
            >
              Close
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  const renderBody = () => {
    switch (emailState.page) {
      case 'verify_otp':
        return renderVerifyOtp()
      case 'update_email':
        return renderUpdateEmail()
      case 'success':
        return renderSuccess()
      case 'send_otp':
      default:
        return renderSendOtp()
    }
  }

  return <FFModal open={props.open}>{renderBody()}</FFModal>
}
export default EmailChangeModel
